import { Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import CustomPagination from "../CustomPagination/CustomPagination";
import PremiumLoader from "../Loader/PremiumLoader";
import { useState, useEffect, useRef, useMemo } from "react";
import "./PRmanager.css";
import { showToast } from "../../Utils";
import Instance from "../../Utils/Axios";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { getPermission } from "../../Utils/method";
import useKeyScroll from "../../Hooks/useKeyScroll";
import useDashboardStore from "../../store/dashboardStore";
import { BuyPrModal } from "../Modal/customModal";

const TruncatedCell = ({ text, id, maxLines = 2 }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (textRef.current) {
        setIsTruncated(
          textRef.current.scrollHeight > textRef.current.clientHeight
        );
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [text]);

  const content = (
    <div
      ref={textRef}
      className="w-100 m-0 p-0"
      style={{
        whiteSpace: "normal",
        wordBreak: "break-word",
        display: "-webkit-box",
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        cursor: isTruncated ? "pointer" : "default",
      }}
    >
      {text}
    </div>
  );

  return isTruncated ? (
    <OverlayTrigger placement="top" overlay={<Tooltip id={id}>{text}</Tooltip>}>
      {content}
    </OverlayTrigger>
  ) : (
    content
  );
};

const getStatusClass = (status) => {
  if (status === "ACTION REQUIRED") return "action_required_btn";
  if (status === "PENDING") return "pending_btn";
  if (status === "DRAFT") return "draft_btn";
  if (status === "REJECT" || status === "REJECTED") return "rejected_btn";
  if (status === "DELETED") return "rejected_btn";
  if (status === "PUBLISHED") return "published_btn";
  return "draft_btn";
};

const PRtable = ({ status, emptyTitle, emptyMessage, filterQuery, onStatsFetch, reportStatus }) => {
  const activeFilter = filterQuery;
  const [prList, setPrList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showViewDetailsModal, setShowViewDetailsModal] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [selectedPRId, setSelectedPRId] = useState(null);
  const [selectedNote, setSelectedNote] = useState({ note: "", date: "" });
  const [showRestrictionModal, setShowRestrictionModal] = useState(false);
  const [restrictionMessage, setRestrictionMessage] = useState("");
  const [restrictionAction, setRestrictionAction] = useState("");
  const [showDownloadReportModal, setShowDownloadReportModal] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [reportDetails, setReportDetails] = useState(null);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const { activePackageCount, fetchDashboardData } = useDashboardStore();
  const scrollContainerRef = useKeyScroll();

  const handleCloseViewDetailsModal = () => setShowViewDetailsModal(false);
  const handleShowViewDetailsModal = (note, date) => {
    setSelectedNote({ note, date });
    setShowViewDetailsModal(true);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr.replace(" ", "T"));
      if (isNaN(date)) return dateStr;
      const day = date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      const time = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return `${day} • ${time}`;
    } catch {
      return dateStr;
    }
  };
  const navigate = useNavigate();

  const handleShowDelete = (id) => {
    setSelectedPRId(id);
    setShowDelete(true);
  };
  const handleNotShowDelete = () => {
    setShowDelete(false);
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("tokenId");
      const response = await Instance.post(
        `user/pressrelease/update`,
        {
          // tokenId: token,
          id: id,
          status: "DELETED",
        }
      );
      if (response.data.resCode === "200") {
        showToast(`PR (ID: ${id}) Deleted successfully!`, "success", "success");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        showToast(response.data.resMessage, "error", "error");
      }
    } catch (error) {
      console.error("Error deleting the company details:", error);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const handleActionClick = (e, list, action, performAction) => {
    const { allowed, message } = getPermission(list.status, action);

    if (!allowed) {
      e.preventDefault();
      setRestrictionMessage(message || `Reports are only available for published PRs.`);
      setRestrictionAction(action);
      setShowRestrictionModal(true);
      return;
    }


    if (performAction) {
      performAction();
    }
  };

  useEffect(() => {
    const fetchPRs = async () => {
      setLoading(true);
      try {
        const payload = {
          pageNumber: currentPage,
          pageSize: rowsPerPage,
        };

        if (status !== undefined) {
          payload.status = status;
        }

        if (reportStatus !== undefined) {
          payload.reportStatus = reportStatus;
        }

        if (sortConfig.key) {
          payload.orderBy = sortConfig.key;
          payload.orderByType = sortConfig.direction === "ascending" ? "ASC" : "DESC";
        }

        if (activeFilter) {
          payload.filterQuery = activeFilter;
        }

        const response = await Instance.post("/user/pressrelease/list", payload);
        const data = response.data?.message || [];
        setPrList(Array.isArray(data) ? data : []);

        if (response.data?.stats && onStatsFetch) {
          onStatsFetch(response.data.stats);
        }

        setTotalPages(response.data?.totalPages || 1);
        setTotalRecords(response.data?.totalRecord || 0);
      } catch (error) {
        console.error("Error fetching PRs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPRs();
  }, [status, currentPage, rowsPerPage, sortConfig, activeFilter, reportStatus]);

  // Reset to page 1 when filter/search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter]);

  useEffect(() => {
    fetchDashboardData().catch(() => { });
  }, [fetchDashboardData]);

  const handleSubmitPr = () => {
    if (activePackageCount === 0) {
      showToast("You don’t have an active credit or package!", "danger", "error");
      setShowBuyModal(true);
    } else {
      navigate("/customer/submit-pr");
    }
  };




  const renderSortIcon = (field) => {
    if (sortConfig.key !== field) {
      return (
        <FontAwesomeIcon
          icon={faArrowDown}
          style={{ marginLeft: 6, opacity: 0.3 }}
        />
      );
    }

    return (
      <FontAwesomeIcon
        icon={sortConfig.direction === "ascending" ? faArrowUp : faArrowDown}
        style={{ marginLeft: 6, opacity: 1 }}
      />
    );
  };

  const handleSortChange = (selectedColumn) => {
    setSortConfig((prev) => ({
      key: selectedColumn.sortField,
      direction:
        prev.key === selectedColumn.sortField &&
          prev.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            ID {renderSortIcon("id")}
          </div>
        ),
        selector: (row) => row.id,
        sortable: true,
        sortField: "id",
        width: "70px",
        cell: (row) => row.id,
      },
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            TITLE {renderSortIcon("title")}
          </div>
        ),
        selector: (row) => row.title,
        sortable: true,
        sortField: "title",
        width: "250px",
        cell: (row, index) => (
          <TruncatedCell
            text={row.title || "N/A"}
            id={`tooltip-pr-title-${index}`}
            maxLines={2}
          />
        ),
      },
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            PACKAGE NAME {renderSortIcon("packageName")}
          </div>
        ),
        selector: (row) => row.packageName,
        sortable: true,
        sortField: "packageName",
        width: "200px",
        cell: (row, index) => (
          <TruncatedCell
            text={row.packageName || "N/A"}
            id={`tooltip-pr-package-${index}`}
            maxLines={2}
          />
        ),
      },
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            COMPANY NAME {renderSortIcon("companyName")}
          </div>
        ),
        selector: (row) => row.companyName,
        sortable: true,
        sortField: "companyName",
        width: "200px",
        cell: (row, index) => (
          <TruncatedCell
            text={row.companyName || "N/A"}
            id={`tooltip-pr-company-${index}`}
            maxLines={2}
          />
        ),
      },
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            CREATED ON {renderSortIcon("updatedOn")}
          </div>
        ),
        selector: (row) => row.updatedOn,
        sortable: true,
        sortField: "updatedOn",
        width: "220px",
        cell: (row) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {formatDateTime(row.updatedOn)}
          </span>
        ),
      },
      {
        name: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            STATUS {renderSortIcon("status")}
          </div>
        ),
        selector: (row) => row.status,
        sortable: true,
        sortField: "status",
        center: true,
        width: "180px",
        style: { justifyContent: "center" },
        headerStyle: { justifyContent: "center" },
        cell: (row) => (
          <div className="pr-table-center-cell">
            <div className={getStatusClass(row.status)}>
              {row.status === "ACTION REQUIRED" ? "ACTION REQUIRED" : row.status}
            </div>
          </div>
        ),
      },
      {
        name: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            ACTIONS
          </div>
        ),
        center: true,
        width: "160px",
        style: { justifyContent: "center" },
        headerStyle: { justifyContent: "center" },
        cell: (row) => (
          <div className="pr-table-center-cell pr-table-actions-cell">
            <Link
              to={`/customer/pr-manager/view/${row.id}`}
              target="_blank"
              onClick={(e) => handleActionClick(e, row, "view")}
            >
              <img
                src="/build/watch.svg"
                alt="View"
                width={24}
                height={24}
              />
            </Link>
            <Link
              to={`/customer/pr-manager/edit/${row.id}`}
              target="_blank"
              onClick={(e) => handleActionClick(e, row, "edit")}
            >
              <img
                src="/build/edit.svg"
                alt="Edit"
                width={24}
                height={24}
              />
            </Link>
            <img
              src="/build/delete.svg"
              alt="Delete"
              width={24}
              height={24}
              onClick={(e) =>
                handleActionClick(e, row, "delete", () => handleShowDelete(row.id))
              }
            />
          </div>
        ),
      },
      {
        name: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            NOTE
          </div>
        ),
        center: true,
        width: "130px",
        style: { justifyContent: "center" },
        headerStyle: { justifyContent: "center" },
        cell: (row) => (
          <div className="pr-table-center-cell">
            {getPermission(row.status, "view").allowed ? (
              <div
                className="pr_note"
                onClick={() =>
                  handleShowViewDetailsModal(row.statusNote, row.statusDate)
                }
                style={{ cursor: "pointer" }}
              >
                View Detail
              </div>
            ) : (
              <div
                className="pr_note"
                onClick={() =>
                  showToast(getPermission(row.status, "view").message, "error", "error")
                }
                style={{ cursor: "pointer" }}
              >
                View Detail
              </div>
            )}
          </div>
        ),
      },
      {
        name: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            REPORT
          </div>
        ),
        center: true,
        width: "120px",
        style: { justifyContent: "center" },
        headerStyle: { justifyContent: "center" },
        cell: (row) => (
          <div className="pr-table-center-cell">
            {row.reportStatus === "YES" ? (
              <div
                className="download_report_btn"
                onClick={() => handleDownloadClick(row.id)}
              >
                Download
              </div>
            ) : (
              <div className="in_process_btn" style={{ cursor: "default" }}>
                In Process
              </div>
            )}
          </div>
        ),
      },
    ],
    [sortConfig]
  );

  const customStyles = {
    table: {
      style: {
        minWidth: "max-content",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        minHeight: "52px",
        minWidth: "max-content",
      },
    },
    headCells: {
      style: {
        fontSize: "13px",
        textTransform: "uppercase",
        fontWeight: "700",
        color: "#444444",
        flexShrink: 0,
        borderRight: "none",
      },
    },
    cells: {
      style: {
        color: "#6c757d",
        fontSize: "14px",
        fontWeight: "500",
        whiteSpace: "normal",
        flexShrink: 0,
        borderRight: "none",
      },
    },
    rows: {
      style: {
        minHeight: "70px",
        minWidth: "max-content",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#f5f6ff",
        outline: "none",
        transition: "background-color 0.2s ease",
      },
    },
  };

  const handleDownloadClick = async (id) => {
    try {
      setLoading(true);
      const response = await Instance.post(`user/pressrelease/report/details`, {
        pressReleaseId: id.toString(),
      });
      if (response.data.resCode === "200") {
        setReportDetails(response.data.message || []);
        setSelectedReportId(id);
        setShowDownloadReportModal(true);
      } else {
        showToast(response.data.resMessage || "Failed to fetch report details", "error", "error");
      }
    } catch (error) {
      console.error("Error fetching report details:", error);
      showToast("Error fetching report details", "error", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (type) => {
    if (!selectedReportId) return;
    try {
      setLoading(true);
      const response = await Instance.get(`user/pressrelease/report/download?pressReleaseId=${selectedReportId}&type=${type}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const extension = type.toUpperCase() === 'EXCEL' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `PR_Report_${selectedReportId}.${extension}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast(`${type} Downloaded Successfully`, "success", "success");
      setShowDownloadReportModal(false);
    } catch (error) {
      console.error("Download error:", error);
      showToast(`Failed to download ${type} report`, "error", "error");
    } finally {
      setLoading(false);
    }
  };

  const currentSortedPR = prList;

  return (
    <>
      <div className="my_pr_tables">
        <div
          className="pr_table_scroll_wrapper"
          ref={scrollContainerRef}
          tabIndex="0"
          style={{ outline: "none" }}
        >
          {loading ? (
            <div className="text-center py-5 d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
              <PremiumLoader fullPage={false} text="Updating list..." />
            </div>
          ) : currentSortedPR?.length === 0 ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5 w-100 pr-table-empty-state">
              <h5 className="text-secondary mb-4">
                {emptyTitle || "Let's submit your first PR"}
              </h5>
              {emptyMessage && (
                <p className="text-muted mb-4">{emptyMessage}</p>
              )}
              {!emptyTitle && (
                <button
                  className="d-flex align-items-center justify-content-center px-4 py-2 fw-bold text-white"
                  style={{
                    background: "#595fde",
                    borderRadius: "25px",
                    fontSize: "14px",
                    lineHeight: "22px",
                    border: "none",
                  }}
                  onClick={handleSubmitPr}
                >
                  Submit PR
                </button>
              )}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={currentSortedPR}
              customStyles={customStyles}
              sortServer
              onSort={handleSortChange}
              sortIcon={<></>}
              highlightOnHover
              pointerOnHover
              dense={false}
              className="pr-table-react"
            />
          )}
        </div>
        <div className="pr-table-footer-pagination">
          <CustomPagination
            totalItems={totalRecords}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </div>
      </div>

      {/* View Details Modal */}
      <Modal
        show={showViewDetailsModal}
        onHide={handleCloseViewDetailsModal}
        centered
        className="pr_view_details_modal"
      >
        <Modal.Body>
          <div className="pr_vd_header">
            <h4 className="pr_modal_head">View Details</h4>
          </div>
          <div className="pr_vd_body">
            <div className="pr_vd_section_label">Note From Editor</div>
            <p className="pr_modal_content">
              {selectedNote.note || <span style={{ color: '#bbb', fontStyle: 'italic' }}>No note available.</span>}
            </p>
          </div>
          <div className="pr_vd_footer">
            <div className="pr_vd_date">
              <span>{formatDateTime(selectedNote.date)}</span>
            </div>
            <Button
              className="modal_cancel_btn"
              onClick={handleCloseViewDetailsModal}
            >
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={showDelete}
        onHide={handleNotShowDelete}
        centered
        className="pr_delete_modal"
      >
        <Modal.Body>
          <div className="">
            <img
              // className={"submit_done_img"}
              src="/build/imageDelete.svg"
              alt="delete icon"
              width={60}
              height={60}
            />
          </div>
          <div>
            <h5 className="submit_modal_head">Delete PR
              {/* (ID: {selectedPRId}) */}
            </h5>
            <p className="submit_modal_content">
              Are you sure you want to delete this PR?
            </p>
          </div>
          <div className="pr_modal_btn">
            <Button
              className="modal_cancel_btn"
              onClick={handleNotShowDelete}
            >
              Cancel
            </Button>
            <Button
              className="pr_delete_confirm_btn"
              onClick={() => {
                handleDelete(selectedPRId);
              }}
            >
              <img
                src="/build/img/Icons/Delete.png"
                alt="delete"
                width={14}
                height={14}
                style={{ marginRight: '6px' }}
              />
              Delete
            </Button>
          </div>
        </Modal.Body>
      </Modal>


      {/* Restriction Message Modal */}
      <Modal
        show={showRestrictionModal}
        onHide={() => setShowRestrictionModal(false)}
        centered
        className="pr_delete_modal"
      >
        <Modal.Body>
          <div className="mt-3">
            <h5 className="submit_modal_head">Action Restricted</h5>
            <p className="submit_modal_content">
              {restrictionMessage}
            </p>
          </div>

          <div className="pr_modal_btn justify-content-center">
            <Button
              className="modal_cancel_btn"
              onClick={() => setShowRestrictionModal(false)}
              style={{ width: '120px' }}
            >
              Okay
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Download Report Modal */}
      <Modal
        show={showDownloadReportModal}
        onHide={() => setShowDownloadReportModal(false)}
        centered
        className="pr_view_details_modal"
      >
        <Modal.Body className="px-4 py-4 text-center position-relative">
          <div
            className="close_modal_icon"
            onClick={() => setShowDownloadReportModal(false)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </div>
          <h4 className="pr_modal_head mb-3">
            Download Report -
            <span className="pr_modal_head_id"> PR ID: {selectedReportId} </span>
          </h4>

          <p className="pr_modal_content mb-4">
            {reportDetails?.some(r => r.fileType === "PDF") && reportDetails?.some(r => r.fileType === "EXCEL")
              ? "Choose the format to download your report"
              : reportDetails?.some(r => r.fileType === "PDF")
                ? "Download your report as PDF"
                : reportDetails?.some(r => r.fileType === "EXCEL")
                  ? "Download your report as Excel"
                  : "Choose the format to download your report"}
          </p>

          <div className="d-flex justify-content-center gap-3 mb-3">
            {reportDetails?.some(report => report.fileType === "PDF") && (
              <Button
                className="modal_save_btn px-4"
                onClick={() => handleDownloadReport("PDF")}
              >
                Download PDF
              </Button>
            )}

            {reportDetails?.some(report => report.fileType === "EXCEL") && (
              <Button
                className="modal_save_btn px-4"
                onClick={() => handleDownloadReport("EXCEL")}
              >
                Download Excel
              </Button>
            )}

            {(!reportDetails || reportDetails.length === 0) && (
              <p className="text-muted">No files available for download.</p>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <BuyPrModal
        show={showBuyModal}
        onClose={() => setShowBuyModal(false)}
        onClick={() => {
          setShowBuyModal(false);
          navigate("/customer/pricing");
        }}
        title="You don’t have an active credit or package!"
        message="Buy a PR credit and start your PR campaign to reach millions of new users."
        primaryBtn="Buy PR Credit"
        icon="/build/img/Package.svg"
      />
    </>
  );
};

export default PRtable;
